import { BuydownScenario } from '@/components/calculator/results/utils/buydownCalculations';
import { BuydownAnalytics, BuilderIncentiveAnalytics } from '@/types/analytics';

export const calculateBuydownAnalytics = (
  scenarios: BuydownScenario[]
): BuydownAnalytics => {
  const scenariosData = scenarios.map(scenario => {
    const valueScore = calculateValueScore(scenario);
    return {
      scenarioType: scenario.type,
      netBenefit: (scenario.totalSavings || 0) - scenario.extraCashNeeded,
      totalSavings: scenario.totalSavings || 0,
      breakEvenMonths: scenario.breakEvenMonths || 0,
      valueScore,
      isRecommended: false,
      monthlyPayment: scenario.paymentSchedule[0]?.monthlyPayment || 0,
      cashToClose: scenario.cashToClose
    };
  });

  const bestScenarioData = scenariosData.reduce((best, current) => 
    current.valueScore > best.valueScore ? current : best
  );

  scenariosData.forEach(s => {
    s.isRecommended = s.scenarioType === bestScenarioData.scenarioType;
  });

  const totalSavings = scenariosData.map(s => s.totalSavings);

  return {
    scenarios: scenariosData,
    bestScenario: bestScenarioData.scenarioType,
    totalSavingsRange: {
      min: Math.min(...totalSavings),
      max: Math.max(...totalSavings)
    },
    optimalStrategy: determineOptimalStrategy(bestScenarioData)
  };
};

const calculateValueScore = (scenario: BuydownScenario): number => {
  const netBenefit = (scenario.totalSavings || 0) - scenario.extraCashNeeded;
  const cashToCloseScore = 1 / (1 + scenario.cashToClose / 10000);
  const breakEvenScore = scenario.breakEvenMonths && scenario.breakEvenMonths > 0
    ? 1 / (1 + scenario.breakEvenMonths / 12)
    : 0;
  
  return (
    netBenefit * 0.5 +
    cashToCloseScore * 25000 +
    breakEvenScore * 10000
  );
};

const determineOptimalStrategy = (bestScenario: any): string => {
  const { scenarioType } = bestScenario;
  
  if (scenarioType === 'fixed') {
    return 'Maximum builder credit with stable payments. Best for long-term ownership.';
  } else if (scenarioType === '1/0') {
    return 'Lower initial payments with quick adjustment. Good for 1-2 year ownership.';
  } else if (scenarioType === '2/1') {
    return 'Gradual payment increases with strong savings. Ideal for 2-3 year ownership.';
  } else {
    return 'Maximum payment savings in early years. Best for 3+ year ownership planning.';
  }
};

export const calculateBuilderIncentiveImpact = (
  scenarios: BuydownScenario[],
  totalClosingCosts: number
): BuilderIncentiveAnalytics => {
  const bestScenario = scenarios.reduce((best, current) => {
    const bestScore = calculateValueScore(best);
    const currentScore = calculateValueScore(current);
    return currentScore > bestScore ? current : best;
  });

  const totalBuilderCredit = bestScenario.builderCredit;
  const originalCashToClose = totalClosingCosts;
  const adjustedCashToClose = bestScenario.cashToClose;
  const savingsAmount = totalBuilderCredit;
  const savingsPercentage = originalCashToClose > 0 ? (savingsAmount / originalCashToClose) * 100 : 0;

  let effectivenessRating: 'Excellent' | 'Good' | 'Fair' | 'Poor';
  if (savingsPercentage >= 50) effectivenessRating = 'Excellent';
  else if (savingsPercentage >= 30) effectivenessRating = 'Good';
  else if (savingsPercentage >= 15) effectivenessRating = 'Fair';
  else effectivenessRating = 'Poor';

  return {
    totalBuilderCredit,
    builderCreditPercentage: bestScenario.builderCreditPercent,
    cashToCloseReduction: savingsPercentage,
    effectivenessRating,
    comparisonWithoutCredits: {
      originalCashToClose,
      adjustedCashToClose,
      savingsAmount,
      savingsPercentage
    }
  };
};
